import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { MultiInputDateRangeField } from '@mui/x-date-pickers-pro/MultiInputDateRangeField';
import { Form, Formik } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';

import { fetchBillingAccounts } from '@sophocles/api/billing';
import FormErrorAlert from '@sophocles/components/FormErrorAlert';
import {
  useConvertPrepaidRadiosMutation,
  useEditPrepaidCoverageMutation,
} from '@sophocles/features/entities/prepaidCoverages';
import { addToast } from '@sophocles/features/toast';
import { applyApiErrorToFormik, parseApiError } from '@sophocles/utils/apiErrors';

// The API validates against the flat coverage payload (start_date, end_date,
// billing_account_id, ...), but only ownership_mode lines up with a Formik
// input by name (dateRange/billing_account are composite/renamed) — every
// other field detail surfaces in the banner instead of on a nonexistent input.
const KNOWN_FIELDS = ['ownership_mode'];

/**
 * Shared edit dialog for one or more prepaid coverages.
 *
 * Props:
 *  - open: bool
 *  - onClose: () => void
 *  - coverages: Array<{ id, start_date?, end_date?, ownership_mode?, billing_account_id?, account_name?, customer_name?, label? }>
 *  - onSuccess: () => void  (called after successful edit)
 */
export function EditCoverageDialog({ open, onClose, coverages = [], onSuccess }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [editPrepaidCoverage] = useEditPrepaidCoverageMutation();

  const [accountOptions, setAccountOptions] = useState([]);
  const [accountSearch, setAccountSearch] = useState('');
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [nonFieldMessages, setNonFieldMessages] = useState([]);

  useEffect(() => {
    if (open) {
      setNonFieldMessages([]);
    }
  }, [open]);

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        dateRange: Yup.array()
          .of(Yup.date().nullable())
          .required()
          .test('start-required', t('billing.prepaidCoverage.startDateRequired'), val => val?.[0] != null)
          .test('end-required', t('billing.prepaidCoverage.endDateRequired'), val => val?.[1] != null),
      }),
    [t],
  );

  const initialValues = useMemo(() => {
    const starts = coverages.map(c => c.start_date).filter(Boolean);
    const ends = coverages.map(c => c.end_date).filter(Boolean);
    const allSameOwnership = coverages.every(c => c.ownership_mode === coverages[0]?.ownership_mode);

    // Resolve billing account ID from multiple possible field shapes. Never
    // fall back to owner.id — that ref names the account's Customer, not
    // the BillingAccount itself, and the two ids are not interchangeable.
    const getAccountId = c =>
      c?.billing_account_id ||
      (typeof c?.billing_account === 'object' ? c.billing_account?.id : c?.billing_account) ||
      null;
    const getAccountLabel = c =>
      c?.account_name || c?.customer_name || c?.owner_name || c?.effective_owner_name || c?.owner?.name;

    const firstAccountId = getAccountId(coverages[0]);
    const allSameAccount = coverages.every(c => getAccountId(c) === firstAccountId);
    const currentAccount =
      allSameAccount && firstAccountId
        ? {
            id: firstAccountId,
            label: getAccountLabel(coverages[0]) || `Account #${firstAccountId}`,
          }
        : null;

    return {
      dateRange: [starts.length ? new Date(starts.sort()[0]) : null, ends.length ? new Date(ends.sort().at(-1)) : null],
      ownership_mode: allSameOwnership ? (coverages[0]?.ownership_mode ?? '') : '',
      billing_account: currentAccount,
    };
  }, [coverages]);

  // Load billing accounts for the autocomplete
  useEffect(() => {
    if (!open) return;
    const loadAccounts = async () => {
      setLoadingAccounts(true);
      try {
        const params = { limit: 25 };
        if (accountSearch) {
          params.search = accountSearch;
        }
        const data = await fetchBillingAccounts(params);
        const results = data.results || data || [];
        setAccountOptions(
          results.map(a => ({
            id: a.id,
            label: a.customer_name
              ? `${a.customer_name} — ${a.name || `Account #${a.id}`}`
              : a.name || `Account #${a.id}`,
          })),
        );
      } catch (err) {
        console.error('Failed to load billing accounts:', err);
      }
      setLoadingAccounts(false);
    };
    const timer = setTimeout(loadAccounts, 300);
    return () => clearTimeout(timer);
  }, [open, accountSearch]);

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    setSubmitting(true);
    setNonFieldMessages([]);
    try {
      const payload = {
        coverage_ids: coverages.map(c => c.id),
        start_date: values.dateRange[0]?.toISOString().split('T')[0],
        end_date: values.dateRange[1]?.toISOString().split('T')[0],
      };
      if (values.ownership_mode) {
        payload.ownership_mode = values.ownership_mode;
      }
      if (values.billing_account?.id) {
        payload.billing_account_id = values.billing_account.id;
      }
      const result = await editPrepaidCoverage({ body: payload });
      if (result.error) throw result.error;
      dispatch(addToast({ message: t('billing.prepaidCoverage.updatedSuccess'), level: 'success' }));
      onClose();
      if (onSuccess) onSuccess();
    } catch (error) {
      setNonFieldMessages(
        applyApiErrorToFormik(error, {
          setErrors,
          knownFields: KNOWN_FIELDS,
          genericMessage: t('billing.prepaidCoverage.updateFailed'),
        }),
      );
    }
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('billing.prepaidCoverage.editDialogTitle')}</DialogTitle>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ errors, touched, isSubmitting, values, setFieldValue }) => (
          <Form>
            <FormErrorAlert messages={nonFieldMessages} sx={{ mx: 3, mt: 1 }} />
            <DialogContent>
              <Stack direction="column" spacing={2} sx={{ mb: coverages.length > 1 ? 3 : 0 }}>
                <DateRangePicker
                  value={values.dateRange}
                  onChange={val => setFieldValue('dateRange', val)}
                  localeText={{
                    start: t('billing.prepaidCoverage.startDate'),
                    end: t('billing.prepaidCoverage.endDate'),
                  }}
                  slots={{ field: MultiInputDateRangeField }}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      error: touched.dateRange && Boolean(errors.dateRange),
                      helperText: touched.dateRange && errors.dateRange,
                    },
                  }}
                />
                <TextField
                  select
                  name="ownership_mode"
                  label={t('billing.prepaidCoverage.ownershipMode')}
                  value={values.ownership_mode}
                  onChange={e => {
                    setFieldValue('ownership_mode', e.target.value);
                    if (e.target.value !== 'fixed_owner') {
                      setFieldValue('billing_account', null);
                    }
                  }}
                  error={touched.ownership_mode && Boolean(errors.ownership_mode)}
                  helperText={touched.ownership_mode && errors.ownership_mode}
                  fullWidth
                  size="small"
                >
                  <MenuItem value="">
                    <em>{t('billing.prepaidCoverage.noChange')}</em>
                  </MenuItem>
                  <MenuItem value="fixed_owner">{t('billing.prepaidCoverage.fixedOwner')}</MenuItem>
                  <MenuItem value="follows_radio">{t('billing.prepaidCoverage.followsRadio')}</MenuItem>
                </TextField>
                {values.ownership_mode === 'fixed_owner' && (
                  <Autocomplete
                    options={accountOptions}
                    loading={loadingAccounts}
                    value={values.billing_account}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    getOptionLabel={option => option?.label || ''}
                    onInputChange={(_, value) => setAccountSearch(value)}
                    onChange={(_, value) => setFieldValue('billing_account', value)}
                    renderInput={params => (
                      <TextField {...params} label={t('billing.prepaidCoverage.ownerLabel')} size="small" fullWidth />
                    )}
                  />
                )}
              </Stack>
              {coverages.length > 1 && (
                <>
                  <Typography variant="caption">
                    {t('billing.prepaidCoverage.editingCount', { count: coverages.length })}
                  </Typography>
                  <ul>
                    {coverages.map(c => (
                      <li key={c.id}>{c.label || t('billing.prepaidCoverage.coverageNumber', { id: c.id })}</li>
                    ))}
                  </ul>
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose} color="secondary">
                {t('actions.cancel')}
              </Button>
              <Button type="submit" color="primary" variant="contained" disabled={isSubmitting}>
                {t('actions.save')}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}

/**
 * Shared convert confirmation dialog for prepaid coverages.
 *
 * Props:
 *  - open: bool
 *  - onClose: () => void
 *  - radios: Array<{ radio_id, coverage_id, label? }>
 *  - onSuccess: () => void  (called after successful convert)
 */
export function ConvertCoverageDialog({ open, onClose, radios = [], onSuccess }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [convertPrepaidRadios] = useConvertPrepaidRadiosMutation();
  const [submitting, setSubmitting] = React.useState(false);
  const [nonFieldMessages, setNonFieldMessages] = React.useState([]);

  useEffect(() => {
    if (open) {
      setNonFieldMessages([]);
    }
  }, [open]);

  const handleConfirm = async () => {
    setSubmitting(true);
    setNonFieldMessages([]);
    try {
      const result = await convertPrepaidRadios({
        body: { radios: radios.map(({ radio_id, coverage_id }) => ({ radio_id, coverage_id })) },
      });
      if (result.error) throw result.error;
      dispatch(addToast({ message: t('billing.prepaidCoverage.convertedSuccess'), level: 'success' }));
      onClose();
      if (onSuccess) onSuccess();
    } catch (error) {
      setNonFieldMessages(
        parseApiError(error, {
          knownFields: [],
          genericMessage: t('billing.prepaidCoverage.convertFailed'),
        }).nonFieldMessages,
      );
    }
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs">
      <DialogTitle>{t('billing.prepaidCoverage.convertDialogTitle')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2">{t('billing.prepaidCoverage.convertConfirmMessage')}</Typography>
        {radios.length > 1 && (
          <>
            <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
              {t('billing.prepaidCoverage.convertingCount', { count: radios.length })}
            </Typography>
            <ul>
              {radios.map(r => (
                <li key={r.coverage_id}>
                  {r.label || t('billing.prepaidCoverage.coverageNumber', { id: r.coverage_id })}
                </li>
              ))}
            </ul>
          </>
        )}
        <FormErrorAlert messages={nonFieldMessages} sx={{ mt: 2 }} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('actions.no')}</Button>
        <Button variant="contained" color="info" onClick={handleConfirm} disabled={submitting}>
          {t('billing.prepaidCoverage.yesConvert')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
